import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { isEqual } from 'lodash';
import { PricingSheetGrid } from './PricingSheetGrid';
import { useAppSelector } from '../../hooks';
import { PricingSheetRegion, PriceColumnsByGroupId } from '../../types/Region';
import { PricingBaseNoteDialog } from '../PricingBaseNoteDialog';
import { PricingContactSupportDialog } from '../PricingContactSupportDialog';
import { getClientIdFromClientSupplier, getConfiguratorFromClientId } from '../../utils/clientIdUtils';
import {
  PricesPerRegionDiff,
  getAvailablePricesPerRegionDiff,
  getSubtitleText,
  hasDefaultRegionFunc,
} from '../../utils/pricingSheetUtils';
import { GridViewType } from '../../constants/GridViewType';
import { PricingSheetAccordion } from './PricingSheetAccordion';
import { PricingSheet as PricingSheetType } from '../../types/PricingSheet';
import { CellMetadata, ClientDataTableRowDiff } from '../../types/ClientData';
import { PricingSheetHeader } from './PricingSheetHeader';
import { Loading } from '../Loading';
import { GridDataType } from '../../constants/PricingBase';

type Props = {
  gridViewType: GridViewType;
  gridDataType?: GridDataType;
  selectedPricingSheetId?: string;
  selectedPricingSheet?: PricingSheetType;
  isLoadingPricingSheetPrices?: boolean;
  pricingSheets: PricingSheetType[];
  regions: PricingSheetRegion[];
  defaultPriceColumn: PriceColumnsByGroupId;
  cellMetadata: CellMetadata[];
  pricingDiffs: {
    table: string;
    changes: ClientDataTableRowDiff[];
  }[];
  accordionsState: { [pricingSheetId: string]: { [index: number]: boolean } };
  onAccordionChange: (index: number) => void;
};

export const PricingSheet: React.FC<Props> = ({
  gridViewType,
  gridDataType,
  selectedPricingSheetId = '',
  selectedPricingSheet,
  isLoadingPricingSheetPrices,
  pricingSheets,
  regions,
  defaultPriceColumn,
  cellMetadata,
  pricingDiffs,
  accordionsState,
  onAccordionChange,
}: Props) => {
  const [subtitleTextPerRegion, setSubtitleTextPerRegion] = React.useState<{ [rowId: string]: string }>({});
  const [availablePricePerRegion, setAvailablePricePerRegion] = React.useState<Map<string, PricesPerRegionDiff>>(
    new Map(),
  );
  const hasDefaultRegion = hasDefaultRegionFunc(regions, defaultPriceColumn);

  const viewerClientId = useAppSelector((state) => state.viewer?.selectedTabId || state.viewer?.selectedClientId || '');
  const clientId = getClientIdFromClientSupplier(viewerClientId);
  const configurator = getConfiguratorFromClientId(clientId);

  useEffect(() => {
    const { prices } = selectedPricingSheet || {};
    if (regions.length > 0 && prices) {
      const newAvailablePricePerRegion = getAvailablePricesPerRegionDiff(
        configurator?.key,
        availablePricePerRegion,
        regions,
        defaultPriceColumn,
        prices,
      );

      let updateSubtitleText = false;
      const newSubtitleTextPerRegion: { [rowId: string]: string } = {};
      regions.forEach((r) => {
        const isHiddenRegion = !!prices?.every(({ hidden = {} }) => hidden[r.regionKey]);
        const text = getSubtitleText(newAvailablePricePerRegion, prices.length, r, defaultPriceColumn, isHiddenRegion);
        if (subtitleTextPerRegion[r.rowId] !== text) {
          updateSubtitleText = true;
        }
        newSubtitleTextPerRegion[r.rowId] = text;
      });

      if (updateSubtitleText) {
        setSubtitleTextPerRegion(newSubtitleTextPerRegion);
      }

      if (!isEqual(newAvailablePricePerRegion, availablePricePerRegion)) {
        setAvailablePricePerRegion(newAvailablePricePerRegion);
      }
    }
  }, [subtitleTextPerRegion, configurator, selectedPricingSheet, availablePricePerRegion, regions]);

  return (
    <Box display="flex" flexDirection="column" flex={1} minWidth="fit-content">
      <PricingSheetHeader selectedPricingSheet={selectedPricingSheet} pricingSheets={pricingSheets} regions={regions} />

      {isLoadingPricingSheetPrices && (
        <div style={{ height: '100%', alignContent: 'center' }}>
          <Loading />
        </div>
      )}

      {!isLoadingPricingSheetPrices && gridViewType === GridViewType.List && (
        <PricingSheetGrid
          gridViewType={gridViewType}
          gridDataType={gridDataType}
          regions={regions}
          defaultPriceColumn={defaultPriceColumn}
          hasDefaultRegion={hasDefaultRegion}
          pricingSheet={selectedPricingSheet}
          cellMetadata={cellMetadata}
          diffs={pricingDiffs}
        />
      )}

      {!isLoadingPricingSheetPrices && regions.length === 1 && gridViewType === GridViewType.Grid && (
        <PricingSheetGrid
          gridViewType={gridViewType}
          gridDataType={gridDataType}
          pricingSheet={selectedPricingSheet}
          defaultPriceColumn={defaultPriceColumn}
          cellMetadata={cellMetadata}
          regions={regions}
          diffs={pricingDiffs}
        />
      )}

      {!isLoadingPricingSheetPrices &&
        regions.length > 1 &&
        gridViewType === GridViewType.Grid &&
        regions.map((region, index) => (
          <PricingSheetAccordion
            key={region.regionKey}
            index={index}
            regions={regions}
            defaultPriceColumn={defaultPriceColumn}
            defaultExpanded={index === 0}
            expanded={accordionsState[selectedPricingSheetId]?.[index] || false}
            handleExpand={onAccordionChange}
            gridViewType={gridViewType}
            gridDataType={gridDataType}
            pricingSheet={selectedPricingSheet}
            cellMetadata={cellMetadata}
            diffs={pricingDiffs}
            subtitleTextPerRegion={subtitleTextPerRegion}
          />
        ))}
      <PricingBaseNoteDialog />
      <PricingContactSupportDialog />
    </Box>
  );
};
